import { Box, type BoxProps } from "../../components/ui/box";
import { Button, type ButtonProps } from "../../components/ui/button";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text, type TextProps } from "../../components/ui/text";
import type React from "react";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";

/**
 * The sidebar pane: the `sidebar`-surface column that fills the sidebar
 * region — a fixed header, a scrolling body of sections, and an optional
 * pinned footer. The region's frame (border, rounding) belongs to the host
 * that carves the region out, not to the pane.
 *
 * ## Each region carries the same 12px gutter, and nothing else does
 *
 * ```
 *   │← 12 →│← 8 →│ text
 *   │gutter│ row │
 *   └region┴ row's own padding = the 20px reading edge
 * ```
 *
 * The gutter lives on the header, the body's content column, and the footer —
 * NOT on the pane — so the body's ScrollArea spans the full pane width and its
 * scrollbar hugs the pane edge instead of floating 12px inside it.
 *
 * Everything inside is full-bleed within that gutter. An interactive box — a
 * menu button, a section heading's collapse trigger, an icon button, the
 * footer button — spans the content column, and its OWN padding is what
 * carries text and glyphs to the reading edge. That is deliberate: a hover
 * wash bleeds the gutter's width past the text it highlights, which is what
 * makes a hovered row read as a row rather than as a box drawn around a label.
 *
 * Two numbers, one owner each. `SidebarGroup` and `SidebarSeparator` are
 * vertical-only for exactly this reason — a group that padded horizontally
 * would push its rows to 24 while the header stayed at 8.
 *
 * Plain text with no interactive box of its own is the one thing that cannot
 * reach the reading edge on its own, so `SidebarPaneTitle` carries that inset.
 */
/**
 * The pane width below which the header runs compact, as a container query
 * against the pane (an inline-size container named `pane`). Under 176px the
 * gutter, identity glyph, caret and the header's always-visible primary
 * action already spend the whole row — a trigger label could render only an
 * ellipsis sliver, and the hover-revealed cluster could only expand over the
 * primary action. Both hide instead, leaving glyph + primary action.
 */
const PANE_COMPACT_HIDDEN = "@max-[176px]/pane:hidden";

export function SidebarPane({
  children,
  border,
  className,
  ...props
}: BoxProps): React.ReactElement {
  return (
    <Box
      surface="sidebar"
      // The pane is an inline-size container: header parts answer the PANE's
      // width (`PANE_COMPACT_HIDDEN`), not the viewport's — the pane is
      // drag-resizable, so the two are unrelated.
      className={cn("@container/pane", className)}
      direction="column"
      fill="flex"
      // Pane-scoped hover group: chrome anywhere in the pane can reveal on
      // pointer entry. The header keeps its own scope for header-only chrome.
      group="pane"
      overflow="hidden"
      // The pane fills a region its HOST frames — inside the shell's content
      // card on desktop, inside the drawer's island wrapper on mobile — so it
      // draws no frame and no rounding of its own. A double edge is exactly
      // what a pane-owned border produces inside any framed host. Consumers
      // composing a free-standing pane pass `border`/`rounded` themselves.
      border={border ?? false}
      // A consumer-passed frame sits against the PAGE, so it takes the page's
      // `--border` — the same hairline the content card draws.
      // `--sidebar-border` (tuned brighter for the pane's own fill) stays for
      // separators inside. Only alongside a real border: Box treats a lone
      // `borderColor` as a request for the full 1px frame.
      borderColor={border ? "border" : undefined}
      data-slot="sidebar-pane"
      {...props}
    >
      {children}
    </Box>
  );
}

export type SidebarPaneHeaderProps = Omittable<{
  /** Leading content — a title, or a back control followed by one. */
  children: React.ReactNode;
  /** Trailing controls, pinned to the pane's far edge. */
  actions?: React.ReactNode;
}>;

/**
 * The pane's fixed header. Sized identically across panes so the title holds
 * its line when a route swaps one pane for another: the block is pinned to
 * 8px padding + the tallest control rung (`Button size="sm"`: 32px, 28px from
 * the `sm` breakpoint), so a header whose actions hold only a 24px icon
 * button — or nothing at all — sits exactly where one with a create button
 * does. The min-height is border-box, so it names the full block, padding
 * included.
 */
export function SidebarPaneHeader({
  children,
  actions,
}: SidebarPaneHeaderProps): React.ReactElement {
  return (
    <Box
      align="center"
      direction="row"
      gap="xs"
      group="header"
      justify="between"
      minHeight={{ base: 48, sm: 44 }}
      // THE gutter (see the pane comment).
      paddingX="base"
      paddingY="sm"
      shrink={false}
    >
      {/* Leading content packs left and absorbs the slack, so the title's
          position does not depend on whether the pane has actions. */}
      <Box align="center" direction="row" gap="xs" grow minWidth={0}>
        {children}
      </Box>
      {actions != null && (
        <Box align="center" direction="row" gap="xs" shrink={false}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

/**
 * The pane's title, carried to the same 20px reading edge the rows share (the
 * 12px gutter plus the rows' own 8px). Plain text has no interactive box of
 * its own to supply that last inset, so this wrapper does. Use it for a title
 * that LEADS the header; a title that follows a control (a back button) is
 * already past the edge and takes a plain `Text`.
 */
export function SidebarPaneTitle({
  children,
  ...props
}: TextProps): React.ReactElement {
  return (
    <Box paddingX="sm" minWidth={0}>
      <Text size="base" truncate weight="semibold" {...props}>
        {children}
      </Text>
    </Box>
  );
}

/**
 * A quiet menu or popover trigger that replaces a plain pane title. It keeps
 * the title reading edge and the header's control height without painting a
 * resting surface. The ghost wash appears only on hover or press. It hugs its
 * content — the header's leading slot absorbs the slack, so the wash never
 * stretches past the identity it belongs to.
 */
export function SidebarPaneHeaderTrigger({
  className,
  shape = "square",
  ...props
}: Omit<ButtonProps, "size" | "variant">): React.ReactElement {
  return (
    <Button
      // `shrink` overrides Button's base `shrink-0`: this is the one button
      // that must give up width when the header's actions need it — without
      // it `min-w-0` is inert, the trigger holds its natural width, and the
      // actions paint OVER its tail once the row runs out of room.
      className={cn("min-w-0 shrink justify-start", className)}
      data-slot="sidebar-pane-header-trigger"
      shape={shape}
      size="sm"
      ink="foreground"
      variant="ghost"
      {...props}
    />
  );
}

/**
 * The header's hover-revealed action cluster — controls that yield their
 * SPACE at rest, not just their ink. A plain `hoverReveal` fades chrome to
 * opacity 0 but keeps reserving its width, so a long header identity
 * truncates against buttons nobody can see, and the reveal then pops in hard
 * against the ellipsis. This wrapper collapses the cluster to zero width at
 * rest — the identity gets the whole row — and slides it open when the header
 * is hovered, a child holds focus or an open popup, or the pointer is coarse
 * (the same conditions the opacity reveal answers), so the identity visibly
 * hands the space back instead of being covered by an overlay.
 *
 * Mechanics: `0fr → 1fr` is the one auto-width interpolation CSS can
 * transition, and it only reaches zero because the inner track child has
 * `min-width: 0` + `overflow: hidden` (an fr track's floor is otherwise its
 * min-content). The 4px padding/negative-margin pair on the inner child keeps
 * a focused control's ring inside the clipped track visible. The `header`
 * hover scope is the one `SidebarPaneHeader` declares.
 *
 * Below the pane's compact width the cluster hides outright: expanded, it
 * could only displace the header's primary action.
 */
export function SidebarPaneHeaderReveal({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box
      className={cn(
        "grid grid-cols-[0fr] transition-[grid-template-columns] duration-base ease-out motion-reduce:transition-none",
        "group-hover/header:grid-cols-[1fr] focus-within:grid-cols-[1fr] has-[[data-popup-open]]:grid-cols-[1fr] pointer-coarse:grid-cols-[1fr]",
        PANE_COMPACT_HIDDEN,
      )}
      data-slot="sidebar-pane-header-reveal"
    >
      <Box
        align="center"
        className="-m-1 min-w-0 overflow-hidden p-1"
        direction="row"
        gap="xs"
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * The truncating identity label inside a `SidebarPaneHeaderTrigger` — the
 * text beside the avatar or glyph. Below the pane's compact width it hides
 * entirely, leaving the glyph and caret as the identity: under that width the
 * label could show at most a character or two, and an ellipsis sliver reads
 * as breakage rather than a name.
 *
 * It is the pane title's rung — 16px semibold, the same as
 * `SidebarPaneTitle` — because the two are one identity in two forms: a pane
 * whose name opens a menu must read as the pane beside it that does not.
 */
export function SidebarPaneHeaderLabel({
  className,
  children,
  ...props
}: TextProps): React.ReactElement {
  return (
    <Text
      className={cn(PANE_COMPACT_HIDDEN, className)}
      size="base"
      truncate
      weight="semibold"
      {...props}
    >
      {children}
    </Text>
  );
}

/**
 * The scrolling section list. Takes whatever the header and pinned footer
 * leave — `minHeight={0}` is what lets it shrink below its content.
 */
export function SidebarPaneBody({
  children,
  label,
}: Omittable<{
  children: React.ReactNode;
  /** Names the body as a navigation landmark ("Settings"), for a pane
   *  whose rows move between pages. A screen reader lists it with the
   *  page's other landmarks. Without it the body is a plain column. */
  label?: string;
}>): React.ReactElement {
  // The ScrollArea is full-bleed so the scrollbar rides the pane edge; the
  // gutters sit on the content column inside it. The bottom gutter is scroll
  // room, not spacing: the last section clears the pane edge (and any pinned
  // footer) instead of ending flush against it, and a list that only just
  // overflows still has somewhere to scroll to.
  return (
    <Box direction="column" grow minWidth={0} minHeight={0}>
      <ScrollArea scrollFade>
        {label ? (
          <Box as="nav" aria-label={label} paddingBottom="2xl" paddingX="base" paddingTop="2xs">
            {children}
          </Box>
        ) : (
          <Box paddingBottom="2xl" paddingX="base" paddingTop="2xs">
            {children}
          </Box>
        )}
      </ScrollArea>
    </Box>
  );
}

/**
 * Pinned footer — outside the scroll region, so scrolling never moves it. Its
 * slot always occupies space, so revealing hover chrome inside it never shifts
 * the sections above.
 */
export function SidebarPaneFooter({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box paddingX="base" paddingY="sm" shrink={false}>
      {children}
    </Box>
  );
}
